// The map app's live values that aren't in the URL (web/state.ts holds what is): one place each, one writer, and
// subscribers for what follows from a change. Pure, so it's unit tested (test/web.test.ts).

type Listener<V> = (value: V) => void;

export class Store<T extends object> {
  private readonly listeners = new Map<keyof T, Set<Listener<never>>>();

  constructor(private readonly values: T) {}

  get<K extends keyof T>(key: K): T[K] {
    return this.values[key];
  }

  /** Set a value; its subscribers run (in the order they subscribed) only when it changed. */
  set<K extends keyof T>(key: K, value: T[K]) {
    if (Object.is(this.values[key], value)) return;
    this.values[key] = value;
    for (const f of [...(this.listeners.get(key) ?? [])]) (f as Listener<T[K]>)(value);
  }

  /** Run `f` after every change of `key`; returns the unsubscribe. */
  on<K extends keyof T>(key: K, f: Listener<T[K]>): () => void {
    let set = this.listeners.get(key);
    if (!set) this.listeners.set(key, (set = new Set()));
    set.add(f as Listener<never>);
    return () => set.delete(f as Listener<never>);
  }
}
